import React, { useState } from 'react';
import { 
  Search, 
  FileText, 
  ExternalLink, 
  MessageSquare, 
  X, 
  Send,
  Loader2,
  BookOpen,
  Hash,
  Sparkles,
  User,
  Clock,
  Brain
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { aiChat } from '../services/api';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';

interface ResearchPaper {
  id: string;
  title: string;
  authors: string;
  journal: string;
  publicationDate: string;
  abstract: string;
  aiSummary: string;
  url: string;
  keywords: string[];
  content: string;
}

interface CategoryTag {
  id: string;
  name: string;
  variant: "default" | "secondary" | "outline" | "destructive";
}

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
}

const MedicalResearch: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<ResearchPaper[]>([]);
  const [selectedPaper, setSelectedPaper] = useState<ResearchPaper | null>(null);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAiResponding, setIsAiResponding] = useState(false);

  // Category tags data
  const categoryTags: CategoryTag[] = [
    { id: '1', name: t('research.categories.cardiovascular'), variant: 'default' },
    { id: '2', name: t('research.categories.diabetes'), variant: 'secondary' },
    { id: '3', name: t('research.categories.neuroscience'), variant: 'outline' },
    { id: '4', name: t('research.categories.oncology'), variant: 'default' },
    { id: '5', name: t('research.categories.infectiousDiseases'), variant: 'destructive' },
  ];

  const getSuggestedQuestions = (paper: ResearchPaper): string[] => {
    if (paper.title.includes('Cardiovascular')) {
      return [
        t('research.suggestedQuestions.cardiovascular.impact'),
        t('research.suggestedQuestions.cardiovascular.mechanism'),
        t('research.suggestedQuestions.cardiovascular.biomarkers'),
        t('research.suggestedQuestions.cardiovascular.surgery')
      ];
    }
    return [
      t('research.suggestedQuestions.default.findings'),
      t('research.suggestedQuestions.default.methods'),
      t('research.suggestedQuestions.default.impact'),
      t('research.suggestedQuestions.default.future')
    ];
  };

  const handleTagClick = (tagName: string) => {
    setSearchQuery(tagName);
    setTimeout(() => handleSearch(), 100);
  };

  const mockPapers: ResearchPaper[] = [
    {
      id: '1',
      title: 'Recent Advances in Cardiovascular Disease Treatment: A Comprehensive Review',
      authors: 'Zhang L, Johnson M, Patel K, et al.',
      journal: 'Journal of Cardiology Research',
      publicationDate: '2023-10-15',
      abstract: 'This review summarizes the latest advances in cardiovascular disease treatment, including novel pharmacological approaches, minimally invasive surgical techniques, and emerging biomarkers for early detection and risk stratification.',
      aiSummary: '本文综述了心血管疾病治疗的最新进展，重点关注三个方面：1) 新型药物治疗方案，特别是SGLT2抑制剂在心力衰竭患者中的应用；2) 微创手术技术的改进，减少了并发症和恢复时间；3) 新兴生物标志物在早期检测和风险分层中的应用。对心内科医生的临床实践具有重要参考价值。',
      url: 'https://pubmed.ncbi.nlm.nih.gov/example1',
      keywords: ['Cardiovascular', 'Treatment', 'Biomarkers'],
      content: 'Detailed content here...'
    },
    {
      id: '2',
      title: 'Artificial Intelligence Applications in Medical Imaging: Current Status and Future Directions',
      authors: 'Wang H, Smith J, Garcia R, et al.',
      journal: 'Radiology Innovations',
      publicationDate: '2023-11-02',
      abstract: 'This paper reviews the current applications of artificial intelligence in medical imaging, focusing on deep learning approaches for image segmentation, classification, and diagnosis support across various imaging modalities.',
      aiSummary: '本文回顾了人工智能在医学影像中的应用现状，主要包括：1) 深度学习在CT、MRI和X光图像分割中的应用；2) 计算机辅助诊断系统在肿瘤检测中的准确性提升；3) 多模态融合技术改进诊断精度。文章指出AI辅助诊断可以显著提高放射科医生的工作效率，但强调AI应作为辅助工具而非替代专业判断。',
      url: 'https://pubmed.ncbi.nlm.nih.gov/example2',
      keywords: ['Artificial Intelligence', 'Medical Imaging', 'Deep Learning'],
      content: 'Detailed content here...'
    }
  ];

  const handleSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      setSearchResults(mockPapers);
      setIsSearching(false);
    }, 1000);
  };

  const handlePaperClick = (paper: ResearchPaper) => {
    setSelectedPaper(paper);
    setShowDiscussion(false);
    setMessages([]);
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedPaper || isAiResponding) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: newMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    const initialAiMessage: Message = {
      id: 'temp-' + Date.now().toString(),
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };
    
    setMessages(prev => [...prev, userMessage, initialAiMessage]);
    setNewMessage('');
    setIsAiResponding(true);
    
    aiChat.sendMessageStream(
      selectedPaper.id,
      newMessage,
      (chunk) => {
        setMessages((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex(m => m.id === initialAiMessage.id);
          if (idx !== -1) updated[idx] = { ...updated[idx], content: updated[idx].content + (chunk.content || '') };
          return updated;
        });
      },
      (messageId) => {
        setMessages((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex(m => m.id === initialAiMessage.id);
          if (idx !== -1) updated[idx] = { ...updated[idx], id: messageId, isStreaming: false };
          return updated;
        });
        setIsAiResponding(false);
      },
      (error) => {
        setIsAiResponding(false);
      },
      'research'
    );
  };

  const handleToggleDiscussion = () => {
    setShowDiscussion(!showDiscussion);
    if (!showDiscussion && messages.length === 0 && selectedPaper) {
      setMessages([{
        id: Date.now().toString(),
        content: t('research.aiGreeting', { title: selectedPaper.title }),
        sender: 'ai',
        timestamp: new Date()
      }]);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('research.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('research.description')}</p>
        </div>
      </div>

      <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white/50 backdrop-blur-sm">
        <CardContent className="p-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder={t('research.searchPlaceholder')} 
                className="pl-9 rounded-xl border-gray-200"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={isSearching} className="rounded-xl px-8">
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              {t('research.search')}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {categoryTags.map((tag) => (
              <Badge 
                key={tag.id} 
                variant={tag.variant} 
                className="cursor-pointer hover:opacity-80 transition-opacity rounded-lg px-3 py-1"
                onClick={() => handleTagClick(tag.name)}
              >
                <Hash className="h-3 w-3 mr-1 opacity-60" />
                {tag.name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden h-fit">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100 py-4">
              <CardTitle className="text-lg flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-primary" />
                {t('research.searchResults') || 'Search Results'}
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[600px]">
              <div className="p-4 space-y-3">
                {isSearching ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
                    <p className="text-sm text-muted-foreground animate-pulse">{t('research.searching')}</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((paper) => (
                    <div
                      key={paper.id}
                      className={`p-4 rounded-xl border transition-all cursor-pointer group ${
                        selectedPaper?.id === paper.id 
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20' 
                          : 'border-gray-100 hover:border-primary/40 hover:bg-gray-50'
                      }`}
                      onClick={() => handlePaperClick(paper)}
                    >
                      <h3 className={`text-sm font-semibold line-clamp-2 group-hover:text-primary transition-colors ${
                        selectedPaper?.id === paper.id ? 'text-primary' : 'text-gray-900'
                      }`}>
                        {paper.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center">
                        <User className="h-3 w-3 mr-1 opacity-60" />
                        {paper.authors.split(',')[0]} et al.
                      </p>
                      <div className="flex items-center justify-between mt-2">
                         <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{paper.journal}</span>
                         <span className="text-[10px] text-gray-400">{paper.publicationDate}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-gray-200" />
                    <p className="mt-2 text-sm text-muted-foreground">{t('research.noResults')}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {selectedPaper ? (
              <motion.div
                key={selectedPaper.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white">
                  <CardHeader className="bg-white border-b border-gray-100 p-6">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <CardTitle className="text-2xl font-bold leading-tight text-gray-900">{selectedPaper.title}</CardTitle>
                        <CardDescription className="text-base mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          <span className="flex items-center"><User className="h-4 w-4 mr-1 text-primary/60" /> {selectedPaper.authors}</span>
                          <span className="flex items-center"><BookOpen className="h-4 w-4 mr-1 text-primary/60" /> {selectedPaper.journal}</span>
                          <span className="flex items-center"><Clock className="h-4 w-4 mr-1 text-primary/60" /> {selectedPaper.publicationDate}</span>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">{t('research.abstract')}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm bg-gray-50/50 p-4 rounded-xl border border-gray-100">{selectedPaper.abstract}</p>
                    </div>
                    
                    <div className="bg-primary/5 rounded-2xl border border-primary/10 p-5 relative overflow-hidden">
                      <Sparkles className="absolute -right-4 -top-4 h-24 w-24 text-primary/5 rotate-12" />
                      <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center">
                        <Brain className="h-4 w-4 mr-2" />
                        {t('research.aiSummary')}
                      </h3>
                      <p className="text-primary/90 text-sm leading-relaxed font-medium">{selectedPaper.aiSummary}</p>
                    </div>

                    <div className="flex gap-3">
                      <Button asChild variant="outline" className="rounded-xl border-gray-200">
                        <a href={selectedPaper.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          {t('research.viewOriginal')}
                        </a>
                      </Button>
                      <Button onClick={handleToggleDiscussion} className="rounded-xl bg-green-600 hover:bg-green-700">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {t('research.discussWithAI')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {showDiscussion && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white">
                      <CardHeader className="bg-gray-50/50 border-b border-gray-100 py-4 flex flex-row items-center justify-between">
                        <CardTitle className="text-lg flex items-center">
                          <Brain className="h-5 w-5 mr-2 text-primary" />
                          {t('research.discussionTitle')}
                        </CardTitle>
                        <Button variant="ghost" size="icon" onClick={() => setShowDiscussion(false)} className="h-8 w-8 rounded-full">
                          <X className="h-4 w-4" />
                        </Button>
                      </CardHeader>
                      <CardContent className="p-0">
                        <ScrollArea className="h-[400px] p-6">
                          <div className="space-y-4">
                            {messages.map((m) => (
                              <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] p-4 rounded-2xl text-sm ${
                                  m.sender === 'user' 
                                    ? 'bg-primary text-primary-foreground rounded-tr-none shadow-md shadow-primary/10' 
                                    : 'bg-gray-100 text-gray-800 rounded-tl-none border border-gray-200'
                                }`}>
                                  <div className="whitespace-pre-line">{m.content}</div>
                                  {m.isStreaming && (
                                    <span className="inline-block w-1.5 h-4 bg-primary/40 animate-pulse ml-1 align-middle" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                      <Separator />
                      <CardFooter className="p-4 bg-gray-50/30">
                        <div className="w-full space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {getSuggestedQuestions(selectedPaper).map((q, i) => (
                              <Button 
                                key={i} 
                                variant="outline" 
                                size="sm" 
                                className="text-[10px] h-7 rounded-full bg-white border-gray-200 hover:border-primary/40 hover:text-primary transition-all"
                                onClick={() => setNewMessage(q)}
                              >
                                {q}
                              </Button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input 
                              placeholder={t('research.askQuestion')} 
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                              className="rounded-xl border-gray-200 bg-white"
                              disabled={isAiResponding}
                            />
                            <Button onClick={handleSendMessage} disabled={!newMessage.trim() || isAiResponding} className="rounded-xl px-6 bg-primary">
                              {isAiResponding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      </CardFooter>
                    </Card>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-center p-8 bg-gray-50/30 rounded-3xl border-2 border-dashed border-gray-100">
                <div className="h-20 w-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-6">
                  <BookOpen className="h-10 w-10 text-gray-200" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">{t('research.selectPaperTitle') || 'Discover Medical Research'}</h2>
                <p className="text-muted-foreground max-w-sm mt-2">
                  {t('research.selectPaperDesc') || 'Search for keywords above and select a research paper to get an AI summary and discuss findings.'}
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default MedicalResearch;
